import { useEffect, useState } from 'react';
import {
  BarChart3,
  Sparkles,
  Loader2,
  Target,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Clock,
  Users,
  ShoppingCart,
  X,
  Check,
  Lightbulb as Bulb
} from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCurrency, formatNumber, formatPercent, pad2 } from '@/lib/utils';
import type { LiveReviewEntity, AISuggestion, AIAnalysisRequest } from '@shared/types';

export default function ReviewPage() {
  const { id } = useParams<{ id?: string }>();
  const [reviews, setReviews] = useState<LiveReviewEntity[]>([]);
  const [activeId, setActiveId] = useState<string | null>(id ?? null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.getLiveReviews().then((list) => {
      setReviews(list);
      if (!activeId && list.length > 0) setActiveId(list[0].id);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => {
    if (id && id !== activeId) setActiveId(id);
  }, [id]);

  const active = reviews.find((r) => r.id === activeId) ?? null;

  const triggerAI = async () => {
    if (!active) return;
    setAiLoading(true);
    try {
      const req: AIAnalysisRequest = {
        liveData: {
          views: active.viewCount,
          gmv: active.gmv,
          conversionRate: active.conversionRate,
          interactRate: active.diagnosis.scores.engagement,
          newFollowers: 500,
          transactionCount: Math.round(active.viewCount * active.conversionRate / 100),
          avgWatchDuration: 90,
          exposureToViewRate: active.diagnosis.scores.traffic / 4,
          duration: 180
        },
        diagnosis: {
          overallScore: active.score,
          overallHealth: active.diagnosis.overallHealth,
          issues: active.diagnosis.issues,
          strengths: active.diagnosis.strengths.map((s) => ({ title: s.title }))
        }
      };
      const res = await api.analyzeLiveReview(req);
      // 更新 review 的 aiSuggestions
      await api.updateLiveReview(active.id, { aiSuggestions: res.suggestions });
      load();
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-foreground font-space uppercase tracking-widest text-sm py-32 text-center">
        LOADING<span className="text-primary">_</span>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="border-2 border-border p-16 text-center">
        <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <div className="text-muted-foreground uppercase tracking-widest">暂无复盘数据</div>
        <div className="text-sm text-muted-foreground mt-2">先去「直播数据」录入一场直播数据,系统会自动生成复盘</div>
      </div>
    );
  }

  return (
    <div>
      <header className="border-b-2 border-border pb-8 mb-8">
        <div className="text-xs uppercase tracking-widest text-primary font-space mb-2">
          REVIEW / {dayjs().format('YYYY.MM.DD')}
        </div>
        <h1 className="text-[clamp(3rem,12vw,8rem)] font-bold leading-[0.85] tracking-tighter uppercase font-space">
          数据<br />复盘<span className="text-primary">.</span>
        </h1>
      </header>

      {/* 场次切换 */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
        {reviews.map((r) => (
          <button
            key={r.id}
            onClick={() => setActiveId(r.id)}
            className={`border-2 border-border px-4 py-3 text-left whitespace-nowrap transition-colors ${
              activeId === r.id ? 'bg-primary text-black border-primary' : 'hover:bg-accent'
            }`}
          >
            <div className="font-bold uppercase tracking-tight">{r.name}</div>
            <div className={`text-xs ${activeId === r.id ? 'text-black/80' : 'text-muted-foreground'}`}>
              {r.date} · {r.hostName}
            </div>
          </button>
        ))}
      </div>

      {active && <ReviewDetail review={active} onTriggerAI={triggerAI} aiLoading={aiLoading} />}
    </div>
  );
}

function ReviewDetail({ review, onTriggerAI, aiLoading }: { review: LiveReviewEntity; onTriggerAI: () => void; aiLoading: boolean }) {
  const healthCls =
    review.diagnosis.overallHealth === 'healthy'
      ? 'border-primary text-primary'
      : review.diagnosis.overallHealth === 'warning'
      ? 'border-warning text-warning'
      : 'border-destructive text-destructive';

  const radarOption = {
    backgroundColor: 'transparent',
    tooltip: { backgroundColor: '#000', borderColor: '#444', textStyle: { color: '#fff' } },
    radar: {
      indicator: review.radarData.map((r) => ({ name: r.dimension, max: 100 })),
      axisName: { color: '#aaa', fontSize: 12 },
      splitArea: { areaStyle: { color: ['#0a0a0d', '#131316'] } },
      splitLine: { lineStyle: { color: '#333' } },
      axisLine: { lineStyle: { color: '#333' } }
    },
    series: [{
      type: 'radar',
      data: [{
        value: review.radarData.map((r) => r.value),
        name: '本场表现',
        lineStyle: { color: '#dafa1d', width: 2 },
        itemStyle: { color: '#dafa1d' },
        areaStyle: { color: 'rgba(218,250,29,0.2)' }
      }]
    }]
  };

  const funnelOption = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', backgroundColor: '#000', borderColor: '#444', textStyle: { color: '#fff' } },
    series: [{
      type: 'funnel',
      data: review.funnelData.map((f, i) => ({
        name: f.stage,
        value: f.value,
        itemStyle: { color: ['#dafa1d', '#67e8f9', '#ec4899', '#f97316'][i] ?? '#fff' }
      })),
      label: { color: '#fff', fontSize: 12 }
    }]
  };

  const kpis = [
    { title: '综合评分', value: `${review.score}`, sub: '/100', icon: Target, accent: healthCls },
    { title: 'GMV', value: formatCurrency(review.gmv), sub: '', icon: ShoppingCart, accent: 'text-foreground' },
    { title: '观看人数', value: formatNumber(review.viewCount), sub: '', icon: Users, accent: 'text-foreground' },
    { title: '转化率', value: formatPercent(review.conversionRate), sub: '', icon: TrendingUp, accent: 'text-foreground' }
  ];

  return (
    <div className="space-y-12">
      {/* 顶部信息 */}
      <section className="border-2 border-border p-6 md:p-8">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-4">
          <div>
            <h2 className="text-3xl md:text-5xl font-bold font-space uppercase leading-none">{review.name}</h2>
            <div className="text-sm text-muted-foreground mt-2 font-space uppercase tracking-widest">
              {review.hostName} · {review.date}
            </div>
          </div>
          <span className={`text-xs font-bold uppercase tracking-widest px-3 py-2 border-2 ${healthCls}`}>
            {review.diagnosis.overallHealth === 'healthy' ? '● 健康' : review.diagnosis.overallHealth === 'warning' ? '● 需改进' : '● 风险'}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 mt-6 border-2 border-border">
          {kpis.map((k) => (
            <div key={k.title} className="border-r-2 border-b-2 last:border-r-0 border-border p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
                <k.icon className="w-4 h-4" /> {k.title}
              </div>
              <div className="flex items-baseline gap-1">
                <span className={`text-3xl md:text-4xl font-bold font-space tracking-tighter ${k.accent}`}>{k.value}</span>
                {k.sub && <span className="text-muted-foreground">{k.sub}</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 雷达 + 漏斗 */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-2 border-border">
        <div className="p-8 md:p-12 border-b-2 lg:border-b-0 lg:border-r-2 border-border">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-4 h-4 bg-primary" />
            <h3 className="text-2xl font-bold font-space uppercase">综合表现雷达</h3>
          </div>
          <ReactECharts option={radarOption} style={{ height: 320 }} />
        </div>
        <div className="p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-4 h-4 bg-primary" />
            <h3 className="text-2xl font-bold font-space uppercase">转化漏斗</h3>
          </div>
          <ReactECharts option={funnelOption} style={{ height: 320 }} />
        </div>
      </section>

      {/* 诊断明细 */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-2 border-border">
        <div className="p-6 md:p-8 border-b-2 lg:border-b-0 lg:border-r-2 border-border">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            <h3 className="text-xl font-bold font-space uppercase">优势</h3>
          </div>
          <ol className="space-y-3">
            {review.diagnosis.strengths.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-space font-bold text-primary tabular-nums">{pad2(i + 1)}</span>
                <div>
                  <div className="font-bold">{s.title}</div>
                  <div className="text-xs text-muted-foreground">{s.description}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="p-6 md:p-8 border-b-2 lg:border-b-0 lg:border-r-2 border-border">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <h3 className="text-xl font-bold font-space uppercase">问题</h3>
          </div>
          <ol className="space-y-3">
            {review.diagnosis.issues.length === 0 ? (
              <li className="text-muted-foreground text-sm">暂无明显问题</li>
            ) : review.diagnosis.issues.map((it, i) => (
              <li key={i} className="flex gap-3">
                <span className={`font-space font-bold tabular-nums ${
                  it.severity === 'high' ? 'text-destructive' : it.severity === 'medium' ? 'text-warning' : 'text-muted-foreground'
                }`}>{pad2(i + 1)}</span>
                <div>
                  <div className="font-bold">{it.title}</div>
                  <div className="text-xs text-muted-foreground">{it.description}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-info" />
            <h3 className="text-xl font-bold font-space uppercase">机会</h3>
          </div>
          <ol className="space-y-3">
            {review.diagnosis.opportunities.map((o, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-space font-bold text-info tabular-nums">{pad2(i + 1)}</span>
                <div>
                  <div className="font-bold">{o.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">{o.potential}</div>
                  <div className="text-xs mt-1"><span className="font-bold uppercase tracking-widest text-info">行动:</span> {o.action}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* AI 优化建议 */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-4 h-4 bg-primary" />
          <h3 className="text-2xl md:text-3xl font-bold font-space uppercase">AI 优化建议</h3>
          <button
            onClick={onTriggerAI}
            disabled={aiLoading}
            className="ml-auto flex items-center gap-2 bg-primary text-black px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-white disabled:opacity-50"
          >
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {review.aiSuggestions.length > 0 ? '重新生成' : '生成 AI 建议'}
          </button>
        </div>

        {review.aiSuggestions.length === 0 ? (
          <div className="border-2 border-border p-8 text-center">
            <Sparkles className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <div className="text-muted-foreground">点击右上角「生成 AI 建议」按钮</div>
            <div className="text-xs text-muted-foreground mt-1">由 Codex 规则引擎根据数据特征生成</div>
          </div>
        ) : (
          <div className="space-y-0 border-2 border-border">
            {review.aiSuggestions.map((s, i) => (
              <SuggestionItem key={i} suggestion={s} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* 行动清单 */}
      {review.forecast?.actionPlan && review.forecast.actionPlan.length > 0 && (
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Target className="w-5 h-5 text-primary" />
            <h3 className="text-2xl md:text-3xl font-bold font-space uppercase">下次行动清单</h3>
          </div>
          <div className="border-2 border-border">
            {review.forecast.actionPlan.map((task, i) => (
              <div
                key={i}
                className={`p-4 border-b-2 last:border-b-0 border-border flex items-start gap-4 hover:bg-primary hover:text-black transition-colors duration-0 cursor-pointer`}
              >
                <span className="font-space font-bold tabular-nums text-2xl opacity-50">{pad2(task.priority)}</span>
                <div className="flex-1">
                  <div className="font-bold">{task.task}</div>
                  <div className="text-xs opacity-70 mt-1 uppercase tracking-widest">
                    {task.category} · {task.expectedImpact}
                  </div>
                </div>
                <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 border-2 ${
                  task.difficulty === 'easy' ? 'border-primary text-primary group-hover:border-black group-hover:text-black' :
                  task.difficulty === 'medium' ? 'border-warning text-warning group-hover:border-black group-hover:text-black' :
                  'border-destructive text-destructive group-hover:border-black group-hover:text-black'
                }`}>
                  {task.difficulty}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SuggestionItem({ suggestion, index }: { suggestion: AISuggestion; index: number }) {
  const accent =
    suggestion.type === 'success'
      ? 'border-primary'
      : suggestion.type === 'warning'
      ? 'border-destructive'
      : suggestion.type === 'improvement'
      ? 'border-info'
      : 'border-muted-foreground';

  return (
    <div className={`p-6 border-b-2 ${accent} last:border-b-0 border-l-4 hover:bg-accent transition-colors duration-200`}>
      <div className="flex items-start gap-4">
        <span className="font-space font-bold text-2xl tabular-nums opacity-50">{pad2(index + 1)}</span>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 border-2 ${accent}`}>
              {suggestion.type}
            </span>
            <span className="font-bold">{suggestion.title}</span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mb-2">{suggestion.content}</p>
          {suggestion.expectedEffect && (
            <div className="text-xs">
              <span className="font-bold uppercase tracking-widest text-primary">预期:</span>{' '}
              <span className="text-muted-foreground">{suggestion.expectedEffect}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
